import { required, type Tournament } from "./model";
import { standings } from "./standings";

export interface Placement {
  teamId: string;
  place: number;
  points: number;
}
export function placements(t: Tournament): Placement[] {
  if (t.status !== "finished") return [];
  const places = new Map<string, number>();
  if (t.config.mode === "swiss" && !t.playoffGenerated) {
    standings(t).forEach((row) => places.set(row.teamId, row.place));
  } else {
    const final = required(t.matches.find((m) => m.stage === "final"));
    if (final.winnerId) places.set(final.winnerId, 1);
    if (final.loserId) places.set(final.loserId, 2);
    const bronze = t.matches.find((m) => m.stage === "bronze");
    if (bronze?.winnerId) places.set(bronze.winnerId, 3);
    if (bronze?.loserId) places.set(bronze.loserId, 4);
    const exits = t.matches
      .filter(
        (m) =>
          m.loserId &&
          !["pool", "swiss", "winner", "final", "bronze"].includes(m.stage),
      )
      .toSorted((a, b) => b.wave - a.wave);
    const waves = [...new Set(exits.map((m) => m.wave))];
    for (const wave of waves) {
      const group = exits.filter(
        (m) => m.wave === wave && !places.has(m.loserId!),
      );
      const place = places.size + 1;
      for (const m of group) places.set(m.loserId!, place);
    }
    const unplaced = t.teams.filter((team) => !places.has(team.id));
    const firstOutside = places.size + 1;
    unplaced.forEach((team) => places.set(team.id, firstOutside));
  }
  t.withdrawals.forEach((w) => {
    if (w.awardPlace) places.set(w.teamId, w.awardPlace);
  });
  return [...places]
    .map(([teamId, place]) => ({
      teamId,
      place,
      points:
        t.config.pointsTable.awards.find((a) => a.place === place)?.points ?? 0,
    }))
    .sort((a, b) => a.place - b.place);
}
export function playerAwards(t: Tournament): Array<{
  playerId: string;
  playerName: string;
  team: string;
  place: number;
  points: number;
}> {
  return placements(t).flatMap((row) => {
    const team = required(t.teams.find((v) => v.id === row.teamId));
    return team.players.map((p) => ({
      playerId: p.id,
      playerName: p.name,
      team: team.name,
      place: row.place,
      points: row.points,
    }));
  });
}
